import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  PendingPackRevealQuery,
  PendingPackRevealQueryVariables,
} from './__generated__/index.graphql.ts';

const PENDING_PACK_QUERY = gql`
  query PendingPackRevealQuery($id: ID!) {
    node(id: $id) {
      id
      ... on Pack {
        id
        claimed
        cards {
          slug
          ...RevealPackCardsDialog_anyCardInterface
        }
        cardPack {
          slug
          pictureUrl
          primaryColor: color(variant: PRIMARY)
          secondaryColor: color(variant: SECONDARY)
          cardSet {
            slug
          }
        }
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<PendingPackRevealQuery, PendingPackRevealQueryVariables>;

type Props = {
  packId: string;
  onDone: () => void;
};

export const PendingPackReveal = ({ packId, onDone }: Props) => {
  const [shouldReveal, setShouldReveal] = useState<boolean | null>(null);
  const doneRef = useRef(false);
  const { data } = useQuery(PENDING_PACK_QUERY, {
    variables: { id: packId },
  });

  const pack = isType(data?.node, 'Pack') ? data?.node : null;

  // `claimed` flips in cache when the cards are claimed mid-reveal: decide once
  if (data && shouldReveal === null) {
    setShouldReveal(!!pack && !pack.claimed);
  }

  const nothingToReveal = !!data && shouldReveal === false;
  useEffect(() => {
    if (!nothingToReveal || doneRef.current) {
      return;
    }
    doneRef.current = true;
    onDone();
  }, [nothingToReveal, onDone]);

  if (!shouldReveal || !pack) return null;

  return (
    <RevealPackCardsDialog
      open
      onContinue={onDone}
      cards={pack.cards ?? []}
      packId={pack.id}
      packPictureUrl={pack.cardPack?.pictureUrl || ''}
      primaryColor={ensureHexColor(pack.cardPack?.primaryColor || '')}
      secondaryColor={ensureHexColor(pack.cardPack?.secondaryColor || '')}
      cardSetSlug={pack.cardPack?.cardSet?.slug || ''}
      showPlayerDetails
    />
  );
};
